/**
 * @file signage.ts
 * @description Signage utilities and session token management for TableGreet Signage.
 */

import type { SignageSettings } from '../types';

export type { SignageSettings };

// Generates a 6-digit numeric PIN for easy TV remote or keypad entry
export const createSignagePassword = () => {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  const digits = Array.from(bytes, byte => byte % 10);
  return digits.join('');
};

export const getSignageUrl = (venueSlug: string, baseUrl = typeof window !== 'undefined' ? window.location.origin : '') => {
  return `${baseUrl.replace(/\/$/, '')}/${venueSlug}/signage`;
};

// Storage session and identity keys
export const getSignageSessionKey = (venueId: string) => `tablegreet_signage_session_${venueId}`;
export const getSignageScreenIdKey = (venueId: string) => `tablegreet_signage_screen_${venueId}`;

// Screens are considered online if their heartbeat was received in the last 90 seconds
export const isScreenOnline = (lastHeartbeatAt?: string | null): boolean => {
  if (!lastHeartbeatAt) return false;
  const heartbeatTime = new Date(lastHeartbeatAt).getTime();
  if (isNaN(heartbeatTime)) return false;
  const diffMs = Date.now() - heartbeatTime;
  return diffMs <= 90 * 1000;
};

// Formats a relative timestamp for display diagnostics
export const formatRelativeTime = (timestamp?: string | null): string => {
  if (!timestamp) return 'Never';
  const time = new Date(timestamp).getTime();
  if (isNaN(time)) return 'Never';
  const diffSeconds = Math.floor((Date.now() - time) / 1000);

  if (diffSeconds < 10) return 'Just now';
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};
